/**
 * AdminFHBFOneComCMS — CMS for the Faith Heights Business Institute one.com site.
 *
 * Mirrors AdminABMHCOneComCMS with FHBF-specific fields (membership tiers,
 * events, institute copy). Full content control from the ecosystem.
 *
 * Public read:  GET /api/v1/fhbf/fhbf/public
 * Admin CRUD:   /api/fhbf/onecom/admin/site/fhbf/*
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Trash2, Plus, RotateCcw, Save, Building2, Users, Mail } from 'lucide-react';
import { getToken } from '@/lib/authToken';
import FhbfEmbedCard from '@/components/fhbf/FhbfEmbedCard';
import FhbfPlatformSettingsCard from '@/components/fhbf/FhbfPlatformSettingsCard';
import SiteConnectionCard from '@/components/SiteConnectionCard';
import { MediaUpload } from '@/components/MediaUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const SLUG = 'fhbf';
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const PAGES = ['about', 'membership', 'events', 'directory', 'village',
                 'sponsors', 'resources', 'contact'];

export default function AdminFHBFOneComCMS() {
  const [site, setSite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const [siteRes, submRes, subsRes] = await Promise.all([
        axios.get(`${API}/api/fhbf/onecom/admin/site/${SLUG}`, { headers: authH() }),
        axios.get(`${API}/api/fhbf/onecom/admin/site/${SLUG}/submissions`, { headers: authH() }),
        axios.get(`${API}/api/fhbf/onecom/admin/site/${SLUG}/subscribers`, { headers: authH() }),
      ]);
      setSite(siteRes.data);
      setSubmissions(submRes.data || []);
      setSubscribers(subsRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load site content');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!site) return;
    setBusy(true);
    try {
      const payload = {
        site: site.site, hero: site.hero, stats: site.stats,
        pages: site.pages, membership_tiers: site.membership_tiers,
        events: site.events, gallery: site.gallery,
      };
      const r = await axios.put(
        `${API}/api/fhbf/onecom/admin/site/${SLUG}`, payload,
        { headers: authH() });
      setSite(r.data);
      toast.success('Saved. Live site refreshes within 5 minutes.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const resetDefaults = async () => {
    if (!confirm('Reset all editable content to defaults? Enquiries and subscribers are NOT affected.')) return;
    try {
      const r = await axios.post(
        `${API}/api/fhbf/onecom/admin/site/${SLUG}/reset`, {},
        { headers: authH() });
      setSite(r.data);
      toast.success('Restored to defaults');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
  };

  if (!site) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-300 p-6 flex items-center justify-center">
        {busy ? 'Loading…' : 'Content not available.'}
      </div>
    );
  }

  const setField = (path, value) => {
    setSite((prev) => {
      const next = structuredClone(prev);
      const parts = path.split('.');
      let ref = next;
      for (let i = 0; i < parts.length - 1; i++) ref = ref[parts[i]];
      ref[parts[parts.length - 1]] = value;
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6" data-testid="admin-fhbf-onecom-page">
      <div className="max-w-5xl mx-auto space-y-4">
        <header className="flex items-center gap-3">
          <Building2 className="h-7 w-7 text-sky-300" />
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-white">FHBI one.com Website CMS</h1>
            <p className="text-sm text-slate-400">
              Faith Heights Business Institute — hero, statistics, membership tiers,
              events, per-page copy, gallery. Updates propagate to the public site within 5 minutes.
            </p>
          </div>
          <Button variant="outline" onClick={resetDefaults}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="fhbf-cms-reset">
            <RotateCcw className="h-4 w-4 mr-1" /> Reset
          </Button>
          <Button onClick={save} disabled={busy} data-testid="fhbf-cms-save">
            <Save className="h-4 w-4 mr-1" /> {busy ? 'Saving…' : 'Save'}
          </Button>
        </header>

        <SiteConnectionCard slug="fhbf" brandColor="sky"
          publicUrl={site.site?.public_url || ''}
          onUrlChange={(u) => setField('site.public_url', u)} />

        <FhbfEmbedCard />

        <FhbfPlatformSettingsCard />

        <Tabs defaultValue="hero">
          <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
            <TabsTrigger value="hero" data-testid="tab-hero">Hero &amp; Site</TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats">Statistics</TabsTrigger>
            <TabsTrigger value="tiers" data-testid="tab-tiers">Membership Tiers</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages">Page Copy</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-subm">Enquiries ({submissions.length})</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subs">Newsletter ({subscribers.length})</TabsTrigger>
          </TabsList>

          {/* HERO & SITE INFO */}
          <TabsContent value="hero" className="space-y-4">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader><CardTitle className="text-sm">Site info</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-3">
                <Field label="Full name" value={site.site.name} onChange={(v) => setField('site.name', v)} testid="fld-site-name" />
                <Field label="Short name" value={site.site.short_name} onChange={(v) => setField('site.short_name', v)} testid="fld-site-short" />
                <Field label="Phone" value={site.site.phone} onChange={(v) => setField('site.phone', v)} testid="fld-site-phone" />
                <Field label="Location" value={site.site.location} onChange={(v) => setField('site.location', v)} testid="fld-site-loc" />
                <Field label="Public site URL (production)" value={site.site.public_url} onChange={(v) => setField('site.public_url', v)} testid="fld-site-public" placeholder="https://faithheightsbusinessfellowship.org" wide />
                <Field label="Main event URL" value={site.site.event_url} onChange={(v) => setField('site.event_url', v)} testid="fld-site-event" placeholder="https://…" wide />
                <Field label="Donation URL (optional)" value={site.site.donation_url} onChange={(v) => setField('site.donation_url', v)} testid="fld-site-donation" placeholder="https://…" wide />
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader><CardTitle className="text-sm">Hero</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Field label="Eyebrow" value={site.hero.eyebrow} onChange={(v) => setField('hero.eyebrow', v)} testid="fld-hero-eyebrow" />
                <Field label="Headline" value={site.hero.title} onChange={(v) => setField('hero.title', v)} testid="fld-hero-title" />
                <FieldArea label="Copy" value={site.hero.copy} onChange={(v) => setField('hero.copy', v)} testid="fld-hero-copy" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* STATS */}
          <TabsContent value="stats">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Statistics (4 tiles on the home page)</CardTitle>
                <Button size="sm" variant="outline" className="border-slate-700"
                        onClick={() => setField('stats', [...site.stats, { value: '0', label: '' }])}
                        data-testid="stats-add"><Plus className="h-3 w-3 mr-1" /> Add</Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {site.stats.map((s, i) => (
                  <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-3 items-end" data-testid={`stat-row-${i}`}>
                    <Field label="Value" value={s.value} onChange={(v) => setField(`stats.${i}.value`, v)} testid={`stat-${i}-value`} placeholder="0" />
                    <Field label="Label" value={s.label} onChange={(v) => setField(`stats.${i}.label`, v)} testid={`stat-${i}-label`} placeholder="Members" />
                    <Button variant="outline" size="sm" className="border-rose-800 text-rose-300 hover:bg-rose-500/10 mb-0.5"
                            onClick={() => setField('stats', site.stats.filter((_, x) => x !== i))}
                            data-testid={`stat-del-${i}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MEMBERSHIP TIERS */}
          <TabsContent value="tiers">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Membership tiers</CardTitle>
                <Button size="sm" variant="outline" className="border-slate-700"
                        onClick={() => setField('membership_tiers', [...site.membership_tiers, {
                          id: `tier-${Date.now()}`, name: '', price: '$0', period: '/year',
                          plan_price: '', plan_period: '', plan_note: '',
                          description: '', featured: false, visibility: 'intro' }])}
                        data-testid="tier-add"><Plus className="h-3 w-3 mr-1" /> Add tier</Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {site.membership_tiers.map((t, i) => (
                  <div key={i} className="border border-slate-800 rounded p-3 bg-slate-800/20 space-y-3"
                       data-testid={`tier-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-[10px]">{t.id}</Badge>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1 text-xs text-slate-400">
                          <Switch checked={!!t.featured}
                                   onCheckedChange={(v) => setField(`membership_tiers.${i}.featured`, v)}
                                   data-testid={`tier-${i}-featured`} />
                          Featured
                        </label>
                        <Button variant="outline" size="sm"
                                className="border-rose-800 text-rose-300 hover:bg-rose-500/10"
                                onClick={() => setField('membership_tiers',
                                  site.membership_tiers.filter((_, x) => x !== i))}
                                data-testid={`tier-del-${i}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      <Field label="Name" value={t.name} onChange={(v) => setField(`membership_tiers.${i}.name`, v)} testid={`tier-${i}-name`} />
                      <Field label="Price" value={t.price} onChange={(v) => setField(`membership_tiers.${i}.price`, v)} testid={`tier-${i}-price`} placeholder="$0" />
                      <Field label="Period" value={t.period} onChange={(v) => setField(`membership_tiers.${i}.period`, v)} testid={`tier-${i}-period`} placeholder="/year" />
                      <Field label="Plan price" value={t.plan_price} onChange={(v) => setField(`membership_tiers.${i}.plan_price`, v)} testid={`tier-${i}-planprice`} />
                      <Field label="Plan period" value={t.plan_period} onChange={(v) => setField(`membership_tiers.${i}.plan_period`, v)} testid={`tier-${i}-planperiod`} />
                      <Field label="Visibility" value={t.visibility} onChange={(v) => setField(`membership_tiers.${i}.visibility`, v)} testid={`tier-${i}-visibility`} placeholder="intro|website|full" />
                    </div>
                    <Field label="Plan note" value={t.plan_note} onChange={(v) => setField(`membership_tiers.${i}.plan_note`, v)} testid={`tier-${i}-plannote`} />
                    <FieldArea label="Description" value={t.description} onChange={(v) => setField(`membership_tiers.${i}.description`, v)} testid={`tier-${i}-desc`} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EVENTS */}
          <TabsContent value="events">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Events</CardTitle>
                <Button size="sm" variant="outline" className="border-slate-700"
                        onClick={() => setField('events', [...(site.events || []), {
                          id: `evt-${Date.now()}`, title: '', date: '', location: '',
                          status: 'Draft', featured: false,
                          registration_url: '', poster_url: '' }])}
                        data-testid="event-add"><Plus className="h-3 w-3 mr-1" /> Add event</Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {(site.events || []).map((e, i) => (
                  <div key={i} className="border border-slate-800 rounded p-3 bg-slate-800/20 space-y-3"
                       data-testid={`event-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-[10px]">{e.id}</Badge>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1 text-xs text-slate-400">
                          <Switch checked={!!e.featured}
                                   onCheckedChange={(v) => setField(`events.${i}.featured`, v)}
                                   data-testid={`event-${i}-featured`} />
                          Featured
                        </label>
                        <Button variant="outline" size="sm"
                                className="border-rose-800 text-rose-300 hover:bg-rose-500/10"
                                onClick={() => setField('events', site.events.filter((_, x) => x !== i))}
                                data-testid={`event-del-${i}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <Field label="Title" value={e.title} onChange={(v) => setField(`events.${i}.title`, v)} testid={`event-${i}-title`} wide />
                      <Field label="Date" value={e.date} onChange={(v) => setField(`events.${i}.date`, v)} testid={`event-${i}-date`} placeholder="10-16 Aug 2026" />
                      <Field label="Location" value={e.location} onChange={(v) => setField(`events.${i}.location`, v)} testid={`event-${i}-loc`} />
                      <Field label="Status" value={e.status} onChange={(v) => setField(`events.${i}.status`, v)} testid={`event-${i}-status`} placeholder="Published" />
                      <Field label="Poster URL" value={e.poster_url} onChange={(v) => setField(`events.${i}.poster_url`, v)} testid={`event-${i}-poster`} />
                      <Field label="Registration URL" value={e.registration_url} onChange={(v) => setField(`events.${i}.registration_url`, v)} testid={`event-${i}-regurl`} wide />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAGE COPY */}
          <TabsContent value="pages" className="space-y-4">
            {PAGES.map((slug) => {
              const p = site.pages?.[slug] || { eyebrow: '', title: '', copy: '' };
              return (
                <Card key={slug} className="bg-slate-900/60 border-slate-800">
                  <CardHeader><CardTitle className="text-sm capitalize">/{slug}</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <Field label="Eyebrow" value={p.eyebrow} onChange={(v) => setField(`pages.${slug}.eyebrow`, v)} testid={`page-${slug}-eyebrow`} />
                    <Field label="Title" value={p.title} onChange={(v) => setField(`pages.${slug}.title`, v)} testid={`page-${slug}-title`} />
                    <FieldArea label="Copy" value={p.copy} onChange={(v) => setField(`pages.${slug}.copy`, v)} testid={`page-${slug}-copy`} />
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Gallery images (public URLs)</CardTitle>
                <Button size="sm" variant="outline" className="border-slate-700"
                        onClick={() => setField('gallery', [...(site.gallery || []),
                          { url: '', alt: '', caption: '' }])}
                        data-testid="gal-add"><Plus className="h-3 w-3 mr-1" /> Add image</Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {(site.gallery || []).length === 0 && (
                  <p className="text-xs text-slate-500">No gallery items yet — click Add image.</p>
                )}
                {(site.gallery || []).map((g, i) => (
                  <div key={i} className="grid grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end" data-testid={`gal-row-${i}`}>
                    <MediaUpload value={g.url} onChange={(url) => setField(`gallery.${i}.url`, url)} purpose="gallery" label="Image" testId={`gal-${i}-url`} previewClassName="h-16" />
                    <Field label="Alt" value={g.alt} onChange={(v) => setField(`gallery.${i}.alt`, v)} testid={`gal-${i}-alt`} />
                    <Field label="Caption" value={g.caption} onChange={(v) => setField(`gallery.${i}.caption`, v)} testid={`gal-${i}-caption`} />
                    <Button variant="outline" size="sm" className="border-rose-800 text-rose-300 hover:bg-rose-500/10"
                            onClick={() => setField('gallery', site.gallery.filter((_, x) => x !== i))}
                            data-testid={`gal-del-${i}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUBMISSIONS */}
          <TabsContent value="submissions">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2">
                <Mail className="h-4 w-4" /> Contact enquiries
              </CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {submissions.length === 0 && <p className="text-xs text-slate-500">No enquiries yet.</p>}
                {submissions.map((s) => (
                  <div key={s.id} className="border border-slate-800 rounded-md p-3 bg-slate-800/30" data-testid={`subm-${s.id}`}>
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>{s.name} · {s.email} · <Badge variant="outline" className="ml-1 text-[10px]">{s.topic}</Badge></span>
                      <span>{s.submitted_at?.slice(0, 19).replace('T', ' ')}</span>
                    </div>
                    <p className="text-sm whitespace-pre-line">{s.message}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUBSCRIBERS */}
          <TabsContent value="subscribers">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4" /> Newsletter subscribers
              </CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {subscribers.length === 0 && <p className="text-xs text-slate-500">No subscribers yet.</p>}
                {subscribers.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border-b border-slate-800 py-1.5 text-sm" data-testid={`sub-${s.id}`}>
                    <span>{s.email}</span>
                    <span className="text-xs text-slate-500">{s.source} · {s.subscribed_at?.slice(0, 10)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, placeholder = '', wide = false }) => (
  <div className={wide ? 'md:col-span-2' : ''}>
    <Label className="text-xs text-slate-400">{label}</Label>
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
           className="bg-slate-900 border-slate-800 mt-1"
           placeholder={placeholder} data-testid={testid} />
  </div>
);

const FieldArea = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs text-slate-400">{label}</Label>
    <Textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)}
              className="bg-slate-900 border-slate-800 mt-1" data-testid={testid} />
  </div>
);
